import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Home from "./pages/Home";
import Services from "./pages/Services";
import Booking from "./pages/Booking";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Admin from "./pages/Admin";
import AdminLogin from "./pages/AdminLogin";
import OrderTracking from "./pages/OrderTracking";
import Location from "./pages/Location";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import ChatbotWidget from "./components/ChatbotWidget";
import ScrollToTop from "./components/ScrollToTop";
import { Toaster } from "./components/ui/toaster";
import { Toaster as SonnerToaster } from "sonner";

// Theme Provider
import { ThemeProvider } from "./contexts/ThemeContext";
import GarageLayout from "./components/GarageLayout";

// Garage (Pitcrew GMS) Pages
import GarageLogin from "./pages/garage/GarageLogin";
import GarageDashboard from "./pages/garage/GarageDashboard";
import JobsList from "./pages/garage/JobsList";
import NewJob from "./pages/garage/NewJob";
import JobDetail from "./pages/garage/JobDetail";
import CreateEstimate from "./pages/garage/CreateEstimate";
import WorkInProgress from "./pages/garage/WorkInProgress";
import InvoiceGenerate from "./pages/garage/InvoiceGenerate";
import InvoiceView from "./pages/garage/InvoiceView";
import Customers from "./pages/garage/Customers";
import GarageVehicles from "./pages/garage/Vehicles";
import GarageProfile from "./pages/garage/GarageProfile";
import Drivers from "./pages/garage/Drivers";
import BookingPageSettings from "./pages/garage/BookingPageSettings";
import GarageBookingPage from "./pages/GarageBookingPage";
import Bookings from "./pages/garage/Bookings";
import FeedbackManagement from "./pages/garage/FeedbackManagement";
import Accounting from "./pages/garage/Accounting";

// Dealers Platform
import DealerLayout from "./pages/dealers/DealerLayout";
import DealerLogin from "./pages/dealers/DealerLogin";
import DealerDashboard from "./pages/dealers/DealerDashboard";
import DealerProfile from "./pages/dealers/DealerProfile";
import DealerAddCar from "./pages/dealers/AddCar";
import DealerApprove from "./pages/dealers/Approve";
import DealerInventory from "./pages/dealers/Inventory";
import DealerCustomers from "./pages/dealers/Customers";
import DealerLeads from "./pages/dealers/DealerLeads";
import MarketplaceLeads from "./pages/dealers/MarketplaceLeads";
import TelecallersPage from "./pages/dealers/TelecallersPage";
import MyDay from "./pages/dealers/MyDay";
import Pipeline from "./pages/dealers/Pipeline";
import Reels from "./pages/dealers/Reels";
import FeatureGate from "./components/dealers/FeatureGate";
import PublicDealerSite from "./pages/dealers/PublicDealerSite";
import PublicCarDetail from "./pages/dealers/PublicCarDetail";

// Customer Portal (Public)
import CustomerJobView from "./pages/customer/CustomerJobView";
import FeedbackPage from "./pages/FeedbackPage";

import CustomDomainGate from "./components/CustomDomainGate";

// Umbrella marketing pages (Aug 2026 — Propitstop becomes a multi-product portal)
import PropitstopHome from "./pages/PropitstopHome";
import UsedCars from "./pages/UsedCars";
import EstimateCheck from "./pages/EstimateCheck";
import Business from "./pages/Business";
import PropitstopDrive from "./pages/PropitstopDrive";
import PitcrewAI from "./pages/PitcrewAI";
import Franchise from "./pages/Franchise";

// Layout wrapper to conditionally show Navbar/Footer
const MainLayout = ({ children }) => {
  const location = useLocation();
  const isGarageRoute = location.pathname.startsWith('/garage');
  const isCustomerPortal = location.pathname.startsWith('/customer');
  const isBookingPage = location.pathname.startsWith('/book/');
  const isFeedbackPage = location.pathname.startsWith('/feedback');
  const isDealerRoute = location.pathname.startsWith('/dealers');
  // Umbrella pages carry their own PropitstopNav / PropitstopFooter — the
  // legacy Navbar / Footer / Chatbot belong ONLY to the /service/* sub-app.
  const isUmbrellaPage = (
    location.pathname === '/' ||
    location.pathname.startsWith('/used-cars') ||
    location.pathname.startsWith('/estimate') ||
    location.pathname.startsWith('/business') ||
    location.pathname.startsWith('/franchise')
  );
  const hideLayout = isGarageRoute || isCustomerPortal || isBookingPage || isFeedbackPage || isDealerRoute || isUmbrellaPage;
  
  return (
    <>
      {!hideLayout && <Navbar />}
      {children}
      {!hideLayout && <Footer />}
      {!hideLayout && <ChatbotWidget />}
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <ScrollToTop />
          <CustomDomainGate>
            <MainLayout>
              <Routes>
              {/* Umbrella marketing pages */}
              <Route path="/" element={<PropitstopHome />} />
              <Route path="/used-cars" element={<UsedCars />} />
              <Route path="/estimate" element={<EstimateCheck />} />
              <Route path="/business" element={<Business />} />
              <Route path="/business/drive" element={<PropitstopDrive />} />
              <Route path="/business/pitcrew" element={<PitcrewAI />} />
              <Route path="/franchise" element={<Franchise />} />

              {/* /service/* — the legacy car-service sub-app */}
              <Route path="/service" element={<Home />} />
              <Route path="/service/services" element={<Services />} />
              <Route path="/service/booking" element={<Booking />} />
              <Route path="/service/about" element={<About />} />
              <Route path="/service/contact" element={<Contact />} />

              {/* Legacy URL redirects — keep old links / SEO working */}
              <Route path="/services" element={<Navigate to="/service/services" replace />} />
              <Route path="/booking" element={<Navigate to="/service/booking" replace />} />
              <Route path="/about" element={<Navigate to="/service/about" replace />} />
              <Route path="/contact" element={<Navigate to="/service/contact" replace />} />

              <Route path="/admin-login" element={<AdminLogin />} />
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute>
                    <Admin />
                  </ProtectedRoute>
                } 
              />
              <Route path="/order/:orderId" element={<OrderTracking />} />
              
              {/* Pitcrew GMS (Garage) Routes - with Layout */}
              <Route path="/garage" element={<GarageLayout />}>
                <Route index element={<GarageLogin />} />
                <Route path="login" element={<GarageLogin />} />
                <Route path="dashboard" element={<GarageDashboard />} />
                <Route path="jobs" element={<JobsList />} />
                <Route path="jobs/new" element={<NewJob />} />
                <Route path="jobs/:jobId" element={<JobDetail />} />
                <Route path="jobs/:jobId/estimate" element={<CreateEstimate />} />
                <Route path="jobs/:jobId/wip" element={<WorkInProgress />} />
                <Route path="jobs/:jobId/invoice/generate" element={<InvoiceGenerate />} />
                <Route path="jobs/:jobId/invoice/:invoiceId" element={<InvoiceView />} />
                <Route path="customers" element={<Customers />} />
                <Route path="vehicles" element={<GarageVehicles />} />
                <Route path="profile" element={<GarageProfile />} />
                <Route path="drivers" element={<Drivers />} />
                <Route path="booking-page" element={<BookingPageSettings />} />
                <Route path="bookings" element={<Bookings />} />
                <Route path="feedback" element={<FeedbackManagement />} />
                <Route path="accounting" element={<Accounting />} />
              </Route>
              
              {/* Public Garage Booking Page */}
              <Route path="/book/:slug" element={<GarageBookingPage />} />

              {/* Propitstop Dealers Platform */}
              <Route path="/dealers/login" element={<DealerLogin />} />
              <Route element={<DealerLayout />}>
                <Route path="/dealers/dashboard" element={<DealerDashboard />} />
                <Route path="/dealers/profile" element={<DealerProfile />} />
                <Route path="/dealers/add-car" element={<DealerAddCar />} />
                <Route path="/dealers/inventory" element={<DealerInventory />} />
                <Route path="/dealers/inventory/:listingId/approve" element={<DealerApprove />} />
                <Route path="/dealers/customers" element={<DealerCustomers />} />
                <Route path="/dealers/leads" element={<DealerLeads />} />
                <Route path="/dealers/marketplace-leads" element={<FeatureGate feature="marketplace_leads_enabled"><MarketplaceLeads /></FeatureGate>} />
                <Route path="/dealers/my-day" element={<FeatureGate feature="marketplace_leads_enabled"><MyDay /></FeatureGate>} />
                <Route path="/dealers/pipeline" element={<FeatureGate feature="marketplace_leads_enabled"><Pipeline /></FeatureGate>} />
                <Route path="/dealers/telecallers" element={<FeatureGate feature="marketplace_leads_enabled"><TelecallersPage /></FeatureGate>} />
                <Route path="/dealers/reels" element={<FeatureGate feature="reels_enabled"><Reels /></FeatureGate>} />
              </Route>
              {/* Public Dealer Microsite */}
              <Route path="/dealers/:slug" element={<PublicDealerSite />} />
              <Route path="/dealers/:slug/cars/:listingId" element={<PublicCarDetail />} />
              
              {/* Customer Portal (Public - No Auth Required) */}
              <Route path="/customer/job/:jobId" element={<CustomerJobView />} />
              
              {/* Feedback Page (Public) */}
              <Route path="/feedback/:token" element={<FeedbackPage />} />
              
              {/* Location-based pages */}
              <Route path="/car-service-koramangala" element={<Location />} />
              <Route path="/car-service-indiranagar" element={<Location />} />
              <Route path="/car-service-whitefield" element={<Location />} />
              <Route path="/car-service-electronic-city" element={<Location />} />
              <Route path="/car-service-hsr-layout" element={<Location />} />
              <Route path="/car-service-btm-layout" element={<Location />} />
              <Route path="/car-service-marathahalli" element={<Location />} />
              <Route path="/car-service-bellandur" element={<Location />} />
              <Route path="/car-service-jayanagar" element={<Location />} />
              <Route path="/car-service-sarjapur-road" element={<Location />} />
              <Route path="/car-service-jp-nagar" element={<Location />} />
              <Route path="/car-service-hebbal" element={<Location />} />
              <Route path="/car-service-rajajinagar" element={<Location />} />
              <Route path="/car-service-malleshwaram" element={<Location />} />
              <Route path="/car-service-yelahanka" element={<Location />} />
            </Routes>
          </MainLayout>
          </CustomDomainGate>
          <Toaster />
          <SonnerToaster position="top-right" richColors />
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
